import type {
  ClinicianTemplate,
  MedicationOption,
  PostVisitAuthorization,
  PostVisitCatalogProduct,
  PostVisitItemAction,
  PostVisitItemStatus,
  PostVisitPurchasedItem,
  PostVisitReviewResponse,
  PostVisitReviewSubmitItem,
  PrescriptionAuthorizationPolicy,
} from "@/types/clinician";
import { initialMedicationProduct } from "./medicationSelection";

export interface PostVisitDraftRow {
  clientKey: string;
  authorizationId: string | null;
  persisted: boolean;
  source: "purchased" | "additional";
  orderId: string | null;
  purchaseProductId: string;
  label: string;
  action: PostVisitItemAction;
  medicationOptions: MedicationOption[];
  medicationProductId: string;
  directionTemplateId: string;
  dosage: string;
  directions: string;
  startDate: string;
  physicianNote: string;
  contraindicationsReviewed: boolean;
  authorizedRefills?: number;
  authorizationConfirmed?: boolean;
  authorizationPolicy?: PrescriptionAuthorizationPolicy | null;
  status: PostVisitItemStatus;
  error: string | null;
}

function purchasedRowKey(orderId: string) {
  return `order:${orderId}`;
}

function additionalRowKey(purchaseProductId: string) {
  return `offer:${purchaseProductId}`;
}

function preferredMedication(options: MedicationOption[]) {
  const selectedId = initialMedicationProduct(options);
  if (selectedId)
    return options.find((option) => option.product_id === selectedId);
  return options.length === 1 ? options[0] : undefined;
}

function preferredDirection(option: MedicationOption | undefined) {
  return (
    option?.direction_templates.find((template) => template.is_default) ??
    option?.direction_templates[0]
  );
}

function preferredNoteTemplate(
  templates: ClinicianTemplate[],
  purchaseProductId: string,
  medicationProductId: string,
) {
  const approved = templates.filter(
    (template) => template.decision === "approve" && template.active,
  );
  const exact = approved.filter(
    (template) =>
      template.product_id === medicationProductId ||
      template.product_id === purchaseProductId,
  );
  const global = approved.filter((template) => !template.product_id);
  return (
    exact.find((template) => template.is_default) ??
    exact[0] ??
    global.find((template) => template.is_default) ??
    global[0]
  );
}

function fillNote(
  template: ClinicianTemplate | undefined,
  patientName: string,
  productLabel: string,
  medicationLabel: string,
) {
  const fallback = `Approved after synchronous clinical visit for ${productLabel}.\n\nClinical assessment: `;
  return (template?.content ?? fallback)
    .replaceAll("{{patient}}", patientName)
    .replaceAll("{{product}}", productLabel)
    .replaceAll("{{medication}}", medicationLabel || productLabel);
}

function draftRow({
  source,
  orderId,
  purchaseProductId,
  label,
  medicationOptions,
  templates,
  patientName,
  clientKey,
}: {
  source: PostVisitDraftRow["source"];
  orderId: string | null;
  purchaseProductId: string;
  label: string;
  medicationOptions: MedicationOption[];
  templates: ClinicianTemplate[];
  patientName: string;
  clientKey: string;
}): PostVisitDraftRow {
  const medication = preferredMedication(medicationOptions);
  const direction = preferredDirection(medication);
  const medicationProductId = medication?.product_id ?? "";
  const noteTemplate = preferredNoteTemplate(
    templates,
    purchaseProductId,
    medicationProductId,
  );
  return {
    clientKey,
    authorizationId: null,
    persisted: false,
    source,
    orderId,
    purchaseProductId,
    label,
    action: source === "purchased" ? "prescribe_now" : "authorize_for_purchase",
    medicationOptions,
    medicationProductId,
    directionTemplateId: direction?.id ?? "",
    dosage: "",
    directions: direction?.content ?? medication?.directions ?? "",
    startDate: "",
    physicianNote: fillNote(
      noteTemplate,
      patientName,
      label,
      medication?.medication ?? medication?.label ?? "",
    ),
    contraindicationsReviewed: false,
    authorizedRefills: medication?.authorization_policy?.refills,
    authorizationConfirmed: false,
    authorizationPolicy: medication?.authorization_policy,
    status: "pending",
    error: null,
  };
}

export function purchasedPostVisitRows(
  items: PostVisitPurchasedItem[],
  templates: ClinicianTemplate[],
  patientName: string,
) {
  return items.map((item) =>
    draftRow({
      source: "purchased",
      orderId: item.order_id,
      purchaseProductId: item.purchase_product_id,
      label: item.label,
      medicationOptions: item.medication_options,
      templates,
      patientName,
      clientKey: purchasedRowKey(item.order_id),
    }),
  );
}

export function additionalPostVisitRow(
  product: PostVisitCatalogProduct,
  templates: ClinicianTemplate[],
  patientName: string,
) {
  return draftRow({
    source: "additional",
    orderId: null,
    purchaseProductId: product.purchase_product_id,
    label: product.label,
    medicationOptions: product.medication_options,
    templates,
    patientName,
    clientKey: additionalRowKey(product.purchase_product_id),
  });
}

function authorizationStatus(
  authorization: PostVisitAuthorization,
): PostVisitItemStatus {
  if (authorization.status === "skipped") return "skipped";
  if (
    authorization.status === "failed" ||
    authorization.status === "cancelled"
  ) {
    return "failed";
  }
  if (authorization.action === "authorize_for_purchase") {
    return ["offered", "purchase_started", "paid"].includes(
      authorization.status,
    )
      ? "authorized"
      : authorization.status === "fulfilled"
        ? "prescribed"
        : "pending";
  }
  return ["used_existing_purchase", "fulfilled"].includes(authorization.status)
    ? "prescribed"
    : "pending";
}

function fallbackMedicationOption(
  authorization: PostVisitAuthorization,
): MedicationOption[] {
  if (!authorization.medication_product_id) return [];
  return [
    {
      product_id: authorization.medication_product_id,
      label:
        authorization.medication_name ?? authorization.medication_product_id,
      medication: authorization.medication_name,
      directions: authorization.directions,
      direction_templates: [],
      partner_key: authorization.partner_key ?? "",
      purchased: authorization.action === "prescribe_now",
    },
  ];
}

function persistedPostVisitRow(
  authorization: PostVisitAuthorization,
  purchasedItems: PostVisitPurchasedItem[],
  catalog: PostVisitCatalogProduct[],
): PostVisitDraftRow {
  const purchased = purchasedItems.find(
    (item) => item.order_id === authorization.source_order_id,
  );
  const product = catalog.find(
    (item) => item.purchase_product_id === authorization.purchase_product_id,
  );
  const medicationOptions = purchased?.medication_options.length
    ? purchased.medication_options
    : product?.medication_options.length
      ? product.medication_options
      : fallbackMedicationOption(authorization);
  return {
    clientKey: authorization.client_key,
    authorizationId: authorization.id,
    persisted: true,
    source:
      authorization.action === "authorize_for_purchase"
        ? "additional"
        : "purchased",
    orderId: authorization.source_order_id,
    purchaseProductId: authorization.purchase_product_id,
    label:
      purchased?.label ?? product?.label ?? authorization.purchase_product_id,
    action: authorization.action,
    medicationOptions,
    medicationProductId: authorization.medication_product_id ?? "",
    directionTemplateId: authorization.direction_template_id ?? "",
    dosage: authorization.dosage ?? "",
    directions: authorization.directions ?? "",
    startDate: authorization.start_date ?? "",
    physicianNote: authorization.physician_note ?? "",
    contraindicationsReviewed: authorization.contraindications_reviewed,
    authorizedRefills: authorization.authorization_policy?.refills,
    authorizationConfirmed: Boolean(authorization.authorization_policy),
    // Never substitute the current catalog policy for a signed snapshot.
    authorizationPolicy: authorization.authorization_policy,
    status: authorizationStatus(authorization),
    error: authorization.error_detail,
  };
}

/** Rebuild the editor from durable server state. Persisted rows retain the
 * exact client key and payload used for the first submission, so a retry can
 * resume external writes without authoring a second note or medication. */
export function postVisitRows(
  review: PostVisitReviewResponse,
  templates: ClinicianTemplate[],
  patientName: string,
): PostVisitDraftRow[] {
  const authorizationsByOrder = new Map(
    review.authorizations
      .filter((authorization) => authorization.source_order_id)
      .map((authorization) => [
        authorization.source_order_id as string,
        authorization,
      ]),
  );
  const included = new Set<string>();
  const rows = review.purchased_items.map((item) => {
    const authorization = authorizationsByOrder.get(item.order_id);
    if (authorization) {
      included.add(authorization.id);
      return persistedPostVisitRow(
        authorization,
        review.purchased_items,
        review.catalog,
      );
    }
    return purchasedPostVisitRows([item], templates, patientName)[0];
  });
  for (const authorization of review.authorizations) {
    if (included.has(authorization.id)) continue;
    rows.push(
      persistedPostVisitRow(
        authorization,
        review.purchased_items,
        review.catalog,
      ),
    );
  }
  return rows;
}

export function postVisitProductMatchesSearch(
  product: PostVisitCatalogProduct,
  search: string,
) {
  const normalized = search.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [
    product.label,
    product.purchase_product_id,
    ...product.medication_options.flatMap((option) => [
      option.label,
      option.medication,
      option.product_id,
      option.directions,
    ]),
  ].some((value) => value?.toLocaleLowerCase().includes(normalized));
}

export function updatePostVisitMedication(
  row: PostVisitDraftRow,
  medicationProductId: string,
  templates: ClinicianTemplate[],
  patientName: string,
) {
  const medication = row.medicationOptions.find(
    (option) => option.product_id === medicationProductId,
  );
  const direction = preferredDirection(medication);
  return {
    ...row,
    medicationProductId,
    dosage: medicationProductId === row.medicationProductId ? row.dosage : "",
    directionTemplateId: direction?.id ?? "",
    directions: direction?.content ?? medication?.directions ?? "",
    physicianNote: fillNote(
      preferredNoteTemplate(
        templates,
        row.purchaseProductId,
        medicationProductId,
      ),
      patientName,
      row.label,
      medication?.medication ?? medication?.label ?? "",
    ),
    contraindicationsReviewed: false,
    authorizedRefills: medication?.authorization_policy?.refills,
    authorizationConfirmed: false,
    authorizationPolicy: medication?.authorization_policy,
    status: "pending" as const,
    error: null,
  };
}

export function postVisitRowError(row: PostVisitDraftRow): string | null {
  if (row.action === "skip") return null;
  if (!row.medicationProductId) return "Choose the exact medication and dose.";
  if (!row.directions.trim()) return "Directions are required.";
  if (!row.physicianNote.trim()) return "A physician note is required.";
  if (
    row.authorizationPolicy &&
    !row.persisted &&
    (!row.authorizationConfirmed ||
      row.authorizedRefills == null ||
      row.authorizedRefills < 0 ||
      row.authorizedRefills > row.authorizationPolicy.refills)
  ) {
    return "Explicitly confirm the prescription refill authorization.";
  }
  if (!row.contraindicationsReviewed) {
    return "Confirm contraindications and clinical evidence were reviewed.";
  }
  return null;
}

export function isFinishedPostVisitRow(row: PostVisitDraftRow) {
  return ["prescribed", "authorized", "skipped"].includes(row.status);
}

export function toPostVisitSubmitItem(
  row: PostVisitDraftRow,
): PostVisitReviewSubmitItem {
  return {
    client_key: row.clientKey,
    action: row.action,
    order_id: row.orderId,
    purchase_product_id: row.purchaseProductId,
    medication_product_id:
      row.action === "skip" ? null : row.medicationProductId || null,
    direction_template_id:
      row.action === "skip" ? null : row.directionTemplateId || null,
    dosage: row.action === "skip" ? null : row.dosage.trim() || null,
    directions: row.action === "skip" ? null : row.directions.trim() || null,
    start_date: row.action === "skip" ? null : row.startDate || null,
    physician_note:
      row.action === "skip" ? null : row.physicianNote.trim() || null,
    contraindications_reviewed:
      row.action === "skip" ? false : row.contraindicationsReviewed,
    ...(row.action !== "skip" && row.authorizationPolicy
      ? {
          authorized_refills: row.authorizedRefills,
          authorization_confirmed: row.authorizationConfirmed,
        }
      : {}),
  };
}
